import { $logger } from '@/boot/logger'
import { useCustomerReturnOrderStore } from '@/stores/customerReturnOrder.store'
import { useCustomerReturnRetailerStore } from '@/stores/customerReturnRetailer.store'
import { useCustomerReturnSessionStore } from '@/stores/customerReturnSession.store'
import { useCustomerReturnConversationStore } from '@/stores/customerReturnConversation.store'
import { useCustomerReturnSubmissionStore } from '@/stores/customerReturnSubmission.store'
import { useCustomerReturnProfileStore } from '@/stores/customerReturnProfile.store'
import { useCustomerReturnPlanStore } from '@/stores/customerReturnPlan.store'
import { useCustomerReturnWrongItemStore } from '@/stores/customerReturnWrongItem.store'
import { useCustomerReturnProductStateStore } from '@/stores/customerReturnProductState.store'
import { useCustomerReturnPackagingStateStore } from '@/stores/customerReturnPackagingState.store'

/**
 * Customer Return Master Store
 *
 * This is the master store for all customer return functionality.
 * It serves as the single entry point for components to:
 * 1. Access all specialized customer return sub-stores
 * 2. Access commonly used reactive state via storeToRefs
 * 3. Orchestrate complex multi-store operations
 * 4. Manage cross-cutting concerns (routing, loading states)
 *
 * Architecture:
 * - Master store pattern: All components access through this store
 * - State access: Read-only reactive state via storeToRefs (Phase 2.3)
 * - Mutations: Via sub-stores (e.g., customerReturn.orderStore.selectItem())
 * - Orchestration: Complex flows managed here (startReturnFlow, initializeAfterItemSelection)
 *
 * Sub-stores:
 * - profileStore: Customer profile and blocked status
 * - orderStore: Order lookup and item selection
 * - retailerStore: Retailer management and selection
 * - sessionStore: Session lifecycle and item processing queue
 * - conversationStore: AI conversation state and flow
 * - submissionStore: Final submission and media uploads
 * - wrongItemStore: Wrong item identification and search
 * - productStateStore: Product state assessment (in-box, assembly)
 * - packagingStateStore: Packaging assessment (box condition, dimensions)
 *
 * @example
 * // Import the master store
 * const customerReturn = useCustomerReturnStore()
 *
 * // Access reactive state (Phase 2.3)
 * const { order, currentItem, retailer } = storeToRefs(customerReturn)
 *
 * // Mutations via sub-stores (clear source)
 * customerReturn.orderStore.selectItem(0)
 * customerReturn.sessionStore.moveToNextItem()
 *
 * // Orchestration methods
 * await customerReturn.startReturnFlow({ email, orderNumber })
 * await customerReturn.initializeAfterItemSelection()
 *
 * @store customerReturn
 */

export const useCustomerReturnStore = defineStore('customerReturn', () => {
  // ============================================================================
  // COMPOSED SUB-STORES
  // ============================================================================

  const profileStore = useCustomerReturnProfileStore()
  const planStore = useCustomerReturnPlanStore()
  const orderStore = useCustomerReturnOrderStore()
  const retailerStore = useCustomerReturnRetailerStore()
  const sessionStore = useCustomerReturnSessionStore()
  const conversationStore = useCustomerReturnConversationStore()
  const submissionStore = useCustomerReturnSubmissionStore()
  const wrongItemStore = useCustomerReturnWrongItemStore()
  const productStateStore = useCustomerReturnProductStateStore()
  const packagingStateStore = useCustomerReturnPackagingStateStore()
  const dynamicQuestionsStore = useCustomerReturnDynamicQuestionsStore()

  // ============================================================================
  // STATE RE-EXPORTS (Read-only reactive state via storeToRefs)
  // ============================================================================
  // These provide convenient access to commonly used state from sub-stores.
  // Components can destructure these for read-only reactive access.
  // For mutations, always use the sub-store methods (e.g., customerReturn.orderStore.selectItem())

  // From profileStore
  const { customerReturnProfile, isBlockedFromReturning } = storeToRefs(profileStore)

  // From planStore
  const { plan, planTierName, isFreeTier, isPremiumTier, features } = storeToRefs(planStore)

  // From orderStore
  const {
    order,
    orderEmail,
    giftGiverEmail,
    orderFound,
    orderNumber,
    items,
    selectedItems,
    hasSelectedItems,
    isGiftReturn,
    isSingleItemOrder,
    isSingleItemReturnable,
  } = storeToRefs(orderStore)

  // From retailerStore
  const { retailer, retailerId, retailersFound, showRetailerSelection, selectedRetailer } = storeToRefs(retailerStore)

  // From sessionStore
  const { currentItem, itemsToProcess, processingItemIndex, orderLookupToken, returnSessionId, nextItem } =
    storeToRefs(sessionStore)

  // From conversationStore
  const {
    conversationStage,
    customerInterface,
    isCustomInterfaceActive,
    customerMessage,
    customerInput,
    complete: conversationComplete,
    handlingUploadPhotos,
    nextQuestion,
  } = storeToRefs(conversationStore)

  // From submissionStore
  const { confirmation, isSubmitted, hasConfirmation, done } = storeToRefs(submissionStore)

  // ============================================================================
  // CROSS-CUTTING STATE
  // ============================================================================

  /**
   * Whether routing/actions are disabled globally
   * @type {Ref<boolean>}
   * @public
   */
  const disableRouting = ref(false)

  /**
   * Loading states for various operations across the flow
   * @type {Ref<Object>}
   * @public
   */
  const loading = ref({
    order: false,
    retailerLookup: false,
    returnSubmission: false,
    mediaUpload: false,
    returnInitialization: false,
    sessionSetup: false,
  })

  /**
   * Whether user has agreed to terms of service
   * @type {Ref<boolean>}
   * @public
   */
  const termsOfServiceAgreed = ref(useUserStore().user?.termsAccepted || false)

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Whether the flow is ready to start (has order and retailer)
   * @returns {boolean}
   * @public
   */
  const isReadyToStart = computed(() => {
    return orderStore.order !== null && retailerStore.retailer !== null
  })

  /**
   * Whether session is initialized and ready for processing
   * @returns {boolean}
   * @public
   */
  const isSessionReady = computed(() => {
    return sessionStore.hasActiveSession && sessionStore.hasItemsToProcess
  })

  /**
   * Whether the entire flow is complete
   * @returns {boolean}
   * @public
   */
  const isFlowComplete = computed(() => {
    return submissionStore.isSubmitted && submissionStore.hasConfirmation
  })

  /**
   * Current flow stage for debugging/monitoring
   * @returns {string}
   * @public
   */
  const currentStage = computed(() => {
    if (isFlowComplete.value) return 'complete'
    if (isSessionReady.value) return 'processing'
    if (isReadyToStart.value) return 'ready'
    if (orderStore.order) return 'order-loaded'
    if (retailerStore.retailer) return 'retailer-selected'
    return 'initial'
  })

  // ============================================================================
  // ORCHESTRATION METHODS
  // ============================================================================

  /**
   * Initialize the return flow from the beginning
   * Handles retailer lookup and automatic order fetch for single-retailer scenarios
   * @param {Object} params - Initialization parameters
   * @param {string} params.email - Customer email
   * @param {string} params.orderNumber - Order number
   * @returns {Promise<Object>} Result with retailers and selection info
   * @public
   */
  async function startReturnFlow({ email, orderNumber }) {
    try {
      // Step 1: Lookup retailer by order
      const result = await retailerStore.lookupByOrder({ email, orderNumber })

      // Step 2: If single retailer, auto-select and fetch order
      if (!result.isMultiple && result.retailers.length === 1) {
        retailerStore.setRetailer(result.retailers[0])
        await orderStore.fetchOrder(result.retailers[0].id, orderNumber)
      }

      return result
    } catch (error) {
      $logger.error('Error starting return flow', error)
      throw error
    }
  }

  /**
   * Initialize session after items have been selected
   * Bridges from order selection to session processing
   * @returns {Promise<boolean>} Success status
   * @public
   */
  async function initializeAfterItemSelection() {
    try {
      if (!isReadyToStart.value) {
        $logger.error('Cannot initialize: Order or retailer missing')
        return false
      }

      // Get selected items
      const selectedItems = orderStore.items.filter(item => item.selected && item.selectedQuantity > 0)

      if (selectedItems.length === 0) {
        $logger.error('No items selected for return')
        return false
      }

      // Initialize session and build processing queue
      const success = await sessionStore.initializeReturnProcess(selectedItems, retailerStore.retailerId, orderStore.orderNumber)

      return success
    } catch (error) {
      $logger.error('Error initializing session', error)
      throw error
    }
  }

  /**
   * Complete the return submission
   * @param {Object} wrongItemsById - Wrong item data by ID
   * @returns {Promise<boolean>} Success status
   * @public
   */
  async function completeSubmission(wrongItemsById = {}) {
    try {
      if (!isSessionReady.value) {
        $logger.error('Cannot submit: Session not ready')
        return false
      }

      // Gather per-item assessments
      const itemsWithAssessments = sessionStore.itemsToProcess.map(item => {
        const packagingAssessment = packagingStateStore.getAssessmentForItem(item.lineItemSessionId)
        const productStateAssessment = productStateStore.getAssessmentForItem(item.lineItemSessionId)

        return {
          ...item,
          packagingAssessment: packagingAssessment?.assessment || null,
          productStateAssessment: productStateAssessment?.assessment || null,
        }
      })

      const success = await submissionStore.submitReturn({
        retailerId: retailerStore.retailerId,
        orderNumber: orderStore.orderNumber,
        returnSessionId: sessionStore.returnSessionId,
        itemsToProcess: itemsWithAssessments,
        wrongItemsById,
      })

      return success
    } catch (error) {
      $logger.error('Error completing submission', error)
      throw error
    }
  }

  // ============================================================================
  // STATE MANAGEMENT METHODS
  // ============================================================================

  /**
   * Reset the entire return flow state
   * Delegates to all sub-stores with coordinated reset
   * @param {Object} options - Reset options
   * @param {boolean} [options.clearConfirmation=false] - Clear confirmation data
   * @param {boolean} [options.keepRetailer=false] - Keep retailer selection
   * @param {boolean} [options.keepOrderEmail=false] - Keep order email
   * @public
   */
  function resetState({ clearConfirmation = false, keepRetailer = false, keepOrderEmail = false } = {}) {
    // Delegate to sub-stores
    profileStore.resetCustomerReturnProfile()
    planStore.resetPlan()
    orderStore.reset({ keepEmail: keepOrderEmail })
    retailerStore.reset({ keepSelection: keepRetailer })
    sessionStore.reset({ clearQueue: true })
    conversationStore.reset({ keepConversationId: false })
    submissionStore.reset({ clearConfirmation })
    wrongItemStore.reset()
    productStateStore.reset()
    packagingStateStore.reset()
    dynamicQuestionsStore.reset()

    // Reset local state
    disableRouting.value = false
    loading.value = {
      order: false,
      retailerLookup: false,
      returnSubmission: false,
      mediaUpload: false,
      returnInitialization: false,
      sessionSetup: false,
    }
    // Note: Don't reset termsOfServiceAgreed as it persists per user
  }

  /**
   * Reset processing state for returning to item selection.
   * Resets session, conversation, submission, wrong item, product state,
   * and packaging state stores while preserving order/retailer/profile context
   * and the orderLookupToken.
   * @public
   */
  function resetForItemReselection() {
    // Preserve the orderLookupToken before resetting
    const preservedToken = sessionStore.orderLookupToken

    // Reset all processing-related stores (not order, retailer, or profile)
    sessionStore.reset({ clearQueue: true })
    conversationStore.reset({ keepConversationId: false })
    submissionStore.reset({ clearConfirmation: false })
    wrongItemStore.reset()
    packagingStateStore.reset()
    productStateStore.reset()
    dynamicQuestionsStore.reset()

    // Restore the orderLookupToken
    sessionStore.setOrderLookupToken(preservedToken)
  }

  /**
   * Set routing disabled state
   * @param {boolean} value - Whether to disable routing
   * @public
   */
  function setDisableRouting(value) {
    disableRouting.value = value
  }

  /**
   * Set terms of service agreement status
   * @param {boolean} value - Whether user agreed to terms
   * @public
   */
  function setTermsOfServiceAgreed(value) {
    termsOfServiceAgreed.value = value
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // Sub-stores (for direct access and mutations)
    profileStore,
    planStore,
    orderStore,
    retailerStore,
    sessionStore,
    conversationStore,
    submissionStore,
    wrongItemStore,
    productStateStore,
    packagingStateStore,
    dynamicQuestionsStore,

    // Re-exported state from profileStore (read-only reactive via storeToRefs)
    customerReturnProfile,
    isBlockedFromReturning,

    // Re-exported state from planStore (read-only reactive via storeToRefs)
    plan,
    planTierName,
    isFreeTier,
    isPremiumTier,
    features,

    // Re-exported state from orderStore (read-only reactive via storeToRefs)
    order,
    orderEmail,
    giftGiverEmail,
    orderFound,
    orderNumber,
    items,
    selectedItems,
    hasSelectedItems,
    isGiftReturn,
    isSingleItemOrder,
    isSingleItemReturnable,

    // Re-exported state from retailerStore (read-only reactive via storeToRefs)
    retailer,
    retailerId,
    retailersFound,
    showRetailerSelection,
    selectedRetailer,

    // Re-exported state from sessionStore (read-only reactive via storeToRefs)
    currentItem,
    itemsToProcess,
    processingItemIndex,
    orderLookupToken,
    returnSessionId,
    nextItem,

    // Re-exported state from conversationStore (read-only reactive via storeToRefs)
    conversationStage,
    customerInterface,
    isCustomInterfaceActive,
    customerMessage,
    customerInput,
    conversationComplete,
    complete: conversationComplete, // Alias for backward compatibility
    handlingUploadPhotos,
    nextQuestion,

    // Re-exported state from submissionStore (read-only reactive via storeToRefs)
    confirmation,
    isSubmitted,
    hasConfirmation,
    done,

    // Cross-cutting state
    disableRouting,
    loading,
    termsOfServiceAgreed,

    // Computed properties
    isReadyToStart,
    isSessionReady,
    isFlowComplete,
    currentStage,

    // Orchestration methods
    startReturnFlow,
    initializeAfterItemSelection,
    completeSubmission,

    // State management
    resetState,
    resetForItemReselection,
    setDisableRouting,
    setTermsOfServiceAgreed,
  }
})
